import * as THREE from 'three';
import type { World } from '../sim/world';
import { Sky, type SkyLighting } from './sky/sky';
import { Ocean } from './ocean/ocean';
import { Pipeline, LAYER_WATER } from './pipeline';
import { atmoUniforms } from './atmosphere';
import { CameraRig } from './cameras';
import { ShipAssets, ShipModel, type ModelEvent } from './ship/shipModel';
import type { Art } from './ship/art';
import { CrewView } from './ship/crewView';
import { RopeSet } from './ship/ropes';
import type { Vessel } from '../sim/vessel';
import { deckHalfWidth, sheerY } from '../data/ship';
import { DEFAULT_LOOK, createLowMaterials } from './materials/materials';

export interface Quality {
  name: string;
  shadowMap: number;
  cloudSteps: number;
  cubeSteps: number;
  skyScale: number;
  pixelRatio: number;
  msaa: number;
}

export const QUALITY: Record<string, Quality> = {
  low: { name: 'Low', shadowMap: 1024, cloudSteps: 24, cubeSteps: 10, skyScale: 0.35, pixelRatio: 0.75, msaa: 0 },
  medium: { name: 'Medium', shadowMap: 2048, cloudSteps: 32, cubeSteps: 14, skyScale: 0.42, pixelRatio: 1, msaa: 2 },
  high: { name: 'High', shadowMap: 4096, cloudSteps: 44, cubeSteps: 18, skyScale: 0.5, pixelRatio: 1, msaa: 4 },
  ultra: { name: 'Ultra', shadowMap: 4096, cloudSteps: 64, cubeSteps: 24, skyScale: 0.65, pixelRatio: 1.5, msaa: 4 },
};

/**
 * Everything visible. Reads the sim each frame and never writes to it.
 */
export class WorldView {
  readonly scene = new THREE.Scene();
  readonly sky: Sky;
  readonly ocean: Ocean;
  readonly pipeline: Pipeline;
  readonly rig: CameraRig;
  readonly keyLight: THREE.DirectionalLight;
  readonly shipModel: ShipModel;
  readonly assets: ShipAssets;
  readonly models = new Map<number, ShipModel>();
  readonly crew: CrewView;
  private readonly grapnels: RopeSet;
  private readonly modelEvents: ModelEvent[] = [];
  private readonly warmups: THREE.Mesh[] = [];
  private lighting: SkyLighting | null = null;
  private readonly shipInv = new THREE.Matrix4();
  private readonly shipVelLocal = new THREE.Vector3();
  private readonly tmpQ = new THREE.Quaternion();
  quality: Quality = QUALITY.high!;
  private width = 1;
  private height = 1;

  constructor(readonly renderer: THREE.WebGLRenderer, private readonly world: World, art: Art | null = null) {
    this.rig = new CameraRig(1);
    this.sky = new Sky(renderer);
    this.ocean = new Ocean(renderer);
    this.pipeline = new Pipeline(renderer);

    this.scene.add(this.sky.background);
    this.ocean.mesh.layers.set(LAYER_WATER);
    this.scene.add(this.ocean.mesh);
    this.assets = new ShipAssets(renderer, true, art);
    this.crew = new CrewView(art?.crew ?? null);
    this.shipModel = new ShipModel(this.assets, world.player, true);
    this.models.set(world.player.id, this.shipModel);
    this.scene.add(this.shipModel.posed);
    this.scene.add(this.crew.group);
    this.grapnels = new RopeSet(this.shipModel.materials.rope, 64);
    this.grapnels.lock();
    this.scene.add(this.grapnels.mesh);
    // Stand-ins so the plain far-off materials compile with everything else
    // at start-up instead of stalling the first ship to cross the mile.
    const low = createLowMaterials(DEFAULT_LOOK);
    const tri = new THREE.BufferGeometry();
    tri.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 0.01, 0, 0, 0, 0.01, 0], 3));
    tri.computeVertexNormals();
    for (const m of [low.hull, low.deck, low.canvas]) {
      const mesh = new THREE.Mesh(tri, m);
      mesh.position.set(0, -5000, 0);
      mesh.receiveShadow = true;
      this.warmups.push(mesh);
      this.scene.add(mesh);
    }

    this.keyLight = new THREE.DirectionalLight(0xffffff, 3);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(4096, 4096);
    const sc = this.keyLight.shadow.camera;
    sc.left = -34;
    sc.right = 34;
    sc.top = 34;
    sc.bottom = -34;
    sc.near = 1;
    sc.far = 400;
    this.keyLight.shadow.bias = -0.0003;
    this.keyLight.shadow.normalBias = 0.03;
    this.keyLight.shadow.radius = 2;
    this.keyLight.layers.enableAll();
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);
  }

  setQuality(q: Quality): void {
    this.quality = q;
    this.keyLight.shadow.mapSize.set(q.shadowMap, q.shadowMap);
    this.keyLight.shadow.map?.dispose();
    this.keyLight.shadow.map = null;
    this.sky.quality = { cloudSteps: q.cloudSteps, cubeSteps: q.cubeSteps, skyScale: q.skyScale };
    this.pipeline.msaa = q.msaa;
    this.resize(this.width, this.height);
  }

  resize(w: number, h: number): void {
    this.width = w;
    this.height = h;
    const pr = Math.min(window.devicePixelRatio, 2) * this.quality.pixelRatio;
    this.renderer.setPixelRatio(pr);
    this.renderer.setSize(w, h);
    const pw = Math.round(w * pr), ph = Math.round(h * pr);
    this.pipeline.setSize(pw, ph);
    this.sky.resize(this.pipeline.renderWidth, this.pipeline.renderHeight);
    this.rig.camera.aspect = w / h;
    this.rig.camera.updateProjectionMatrix();
    this.ocean.setSceneTextures(this.pipeline.opaqueColor, this.pipeline.opaqueDepth, this.pipeline.renderWidth, this.pipeline.renderHeight);
  }

  /** Internal render resolution scale (dynamic resolution). */
  setRenderScale(scale: number): void {
    this.pipeline.setScale(scale);
    this.sky.resize(this.pipeline.renderWidth, this.pipeline.renderHeight);
    this.ocean.setSceneTextures(this.pipeline.opaqueColor, this.pipeline.opaqueDepth, this.pipeline.renderWidth, this.pipeline.renderHeight);
  }

  /** Drop the shader warm-up stand-ins once everything has compiled. */
  endWarmup(): void {
    for (const m of this.warmups) m.removeFromParent();
    this.warmups.length = 0;
  }

  /** The player's ship, posed in the world (ship-local frame). */
  get ship(): THREE.Group {
    return this.shipModel.posed;
  }

  /** Create models for new arrivals, drop the departed, pose them all. */
  syncShips(alpha: number): void {
    const present = new Set<number>();
    for (const v of this.world.vessels) {
      present.add(v.id);
      let m = this.models.get(v.id);
      if (!m) {
        m = new ShipModel(this.assets, v, false);
        this.models.set(v.id, m);
        this.scene.add(m.posed);
      }
      m.sync(alpha);
    }
    for (const [id, m] of this.models) {
      if (present.has(id)) continue;
      m.dispose();
      this.models.delete(id);
    }
  }

  /** Splashes and crashes the models noticed since last asked. */
  drainModelEvents(): ModelEvent[] {
    for (const m of this.models.values()) {
      this.modelEvents.push(...m.events);
      m.events.length = 0;
    }
    return this.modelEvents.splice(0);
  }

  modelOf(v: Vessel): ShipModel | undefined {
    return this.models.get(v.id);
  }

  update(dt: number): void {
    const w = this.world;
    const cam = this.rig.camera;
    const p = w.weather.params;

    // Sky and derived lighting.
    const sunDir = new THREE.Vector3(w.day.sunDir.x, w.day.sunDir.y, w.day.sunDir.z);
    const moonDir = new THREE.Vector3(w.day.moonDir.x, w.day.moonDir.y, w.day.moonDir.z);
    const flash = new THREE.Vector4(0, 0, 0, 0);
    if (w.weather.lightning) {
      const l = w.weather.lightning;
      flash.set(l.x, p.cloudBase + 400, l.z, w.weather.flash);
    }
    const L = this.sky.update({
      camera: cam, sunDir, moonDir, weather: p, windX: w.wind.dirX, windZ: w.wind.dirZ, windSpeed: w.wind.speed,
      dt, time: w.time, flash,
    });
    this.lighting = L;
    this.scene.environment = this.sky.environment;
    this.scene.environmentIntensity = 1;

    // Key light follows the ship so its shadow map stays tight.
    this.keyLight.color.copy(L.keyColor);
    this.keyLight.intensity = 1;
    this.keyLight.target.position.copy(this.ship.position);
    this.keyLight.position.copy(this.ship.position).addScaledVector(L.keyDir, 200);
    this.keyLight.target.updateMatrixWorld();

    atmoUniforms.uSunDir.value.copy(L.keyDir);
    atmoUniforms.uSunColor.value.copy(L.keyColor);
    atmoUniforms.uSkyAmbient.value.copy(L.ambient);
    atmoUniforms.uHaze.value = 3.2e-5 + p.haze * 2.4e-4 + p.rain * 3e-4;
    atmoUniforms.uFog.value = p.fog * p.fog * 0.012 + p.rain * 0.0009;
    atmoUniforms.uFogHeight.value = 40 + p.fog * 60 + p.rain * 200;
    atmoUniforms.uFogBanks.value = p.fog;
    atmoUniforms.uFogDrift.value.x -= w.wind.dirX * w.wind.speed * dt;
    atmoUniforms.uFogDrift.value.y -= w.wind.dirZ * w.wind.speed * dt;
    atmoUniforms.uFlash.value.copy(flash);
    atmoUniforms.uTime.value = w.time;

    // Ships and their people.
    const pixelAngle = (2 * Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2)) / Math.max(1, this.pipeline.renderHeight);
    for (const m of this.models.values()) m.update(dt, L.night, pixelAngle, w.wetness, cam.position);
    // No crew drawn on ships far enough off to be drawn as their basic build.
    this.crew.update(w.vessels, (v) => {
      const m = this.models.get(v.id);
      return m && !m.low ? m.posed.matrixWorld : null;
    }, cam.position, w.time);
    this.updateGrapnels();

    // Ocean.
    this.shipInv.copy(this.ship.matrixWorld).invert();
    const s = w.ship;
    this.shipVelLocal.set(s.vel.x, s.vel.y, s.vel.z).applyQuaternion(this.tmpQ.copy(this.ship.quaternion).invert());
    this.ocean.update(this.renderer, {
      camera: cam, waves: w.waves, time: w.time, dt, windSpeed: w.wind.speed, windX: w.wind.dirX, windZ: w.wind.dirZ,
      rain: p.rain, shipX: s.com.x, shipZ: s.com.z, shipMatrixInverse: this.shipInv, shipVelLocal: this.shipVelLocal,
      shipHeave: s.vel.y, night: L.night,
    });
  }

  /** Grappling lines between ships locked together for boarding. */
  private updateGrapnels(): void {
    const R = this.grapnels;
    R.beginFrame();
    const w = this.world;
    for (const a of w.vessels) {
      if (a.boardingWith < 0) continue;
      const b = w.vessels.find((v) => v.id === a.boardingWith);
      const ma = this.models.get(a.id), mb = b ? this.models.get(b.id) : undefined;
      if (!b || !ma || !mb || a.id > b.id) continue;
      for (let i = 0; i < 6; i++) {
        const xa = -9 + i * 4.2, xb = -8 + i * 3.9;
        const pa = new THREE.Vector3(xa * a.ship.sx, (sheerY(xa) + 0.2) * a.ship.sy, a.boardingSide * (deckHalfWidth(xa) + 0.1) * a.ship.sz).applyMatrix4(ma.posed.matrixWorld);
        const pb = new THREE.Vector3(xb * b.ship.sx, (sheerY(xb) + 0.2) * b.ship.sy, b.boardingSide * (deckHalfWidth(xb) + 0.1) * b.ship.sz).applyMatrix4(mb.posed.matrixWorld);
        R.sagging(pa, pb, 0.02, 0.6, 5);
      }
    }
    R.endFrame();
  }

  render(dt: number): void {
    const L = this.lighting;
    if (!L) return;
    const w = this.world;
    const ps = this.pipeline.settings;
    ps.focus = this.rig.focus;
    ps.dofAmount = this.rig.dofAmount;
    ps.night = L.night;
    // Warm light at golden hour, cool under overcast and at night.
    const el = w.day.sunElevation;
    const golden = THREE.MathUtils.smoothstep(el, -0.05, 0.05) * (1 - THREE.MathUtils.smoothstep(el, 0.12, 0.4));
    ps.warmth = golden * 0.8 - w.weather.params.stratus * 0.3 - L.night * 0.4;
    ps.saturation = 1.0 - w.weather.params.stratus * 0.12 - w.weather.params.fog * 0.1;
    // Heavy weather should read dark; don't let the eye adapt it away.
    ps.exposureBias = -(w.weather.params.stratus * 0.35 + w.weather.params.rain * 0.75);
    ps.flash = w.weather.flash;
    this.pipeline.render(this.scene, this.rig.camera, dt, w.time, {
      skyTexture: this.sky.background.material instanceof THREE.ShaderMaterial ? (this.sky.background.material.uniforms.uSkyTex!.value as THREE.Texture) : null,
      sunDir: L.keyDir,
      sunColor: L.keyIsSun ? L.keyColor : new THREE.Color(0, 0, 0),
      haze: w.weather.params.haze + w.weather.params.fog,
    }, this.ship.matrixWorld, L.night);
  }
}
